// src/config/plans.js
// 📊 Single Source of Truth - Preços e Planos PropZyn
// Arquitetura Enterprise: 1 arquivo → Frontend + Backend + Webhook
// Inspirado em: Stripe, Notion, Linear, Vercel
// Versão: 2.0 | 31/05/2026
// Melhorias: Validação defensiva, suporte anual completo, tipagem JSDoc

/**
 * ════════════════════════════════════════════
 * ESTRATÉGIA DE PREÇOS - LANÇAMENTO
 * ════════════════════════════════════════════
 * 
 * Early Adopters: primeiros 50 usuários de cada plano
 * recebem desconto de ~50% VITALÍCIO.
 * 
 * Após 50 vendas em cada plano, preço volta ao normal.
 * Contador controlado via Firestore (plans/launchCounters).
 * 
 * Preços em CENTAVOS (padrão Stripe) para evitar bugs de float IEEE 754.
 */

/** @constant {number} Cupos de lançamento por plano */
export const LAUNCH_LIMIT = 50;

/**
 * @constant {Object} PLANS
 * Catálogo oficial de planos - Single Source of Truth
 * Usado por: api/plans.js, api/payments.js, PlanosPage.jsx, usePlanAccess.js
 */
export const PLANS = {
  basic: {
    id: 'basic',
    name: 'Básico',
    subtitle: 'Para começar',
    color: 'blue',
    
    // Preços em CENTAVOS (evita bugs de float - padrão Stripe)
    prices: {
      monthly: {
        regular: 9700,    // R$ 97,00
        launch: 4700,     // R$ 47,00 (lançamento ~51.5% desconto)
      },
      annual: {
        regular: 77600,   // R$ 776/ano (R$ 64,67/mês - 2 meses grátis)
        launch: 37600,    // R$ 376/ano (R$ 31,33/mês)
      }
    },
    
    limits: {
      imoveis: 10,
      clientes: 50,
      usuarios: 1,
      documentos: 100,
      whiteLabel: false,
      api: false,
      suporte: 'email'
    },
    
    features: [
      { text: 'Até 10 imóveis', included: true },
      { text: 'Até 50 clientes', included: true },
      { text: '1 usuário', included: true },
      { text: 'Calendário de visitas', included: true },
      { text: 'Relatórios básicos', included: true },
      { text: 'Suporte por email', included: true },
      { text: 'API de integração', included: false },
      { text: 'White Label', included: false },
      { text: 'Relatórios avançados', included: false },
    ],
    
    metadata: {
      isPopular: false,
      isBestValue: false,
      badge: null,
      cta: 'Começar agora',
      highlight: null,
      icon: '🚀'
    }
  },
  
  professional: {
    id: 'professional',
    name: 'Profissional',
    subtitle: 'O mais popular',
    color: 'indigo',
    
    prices: {
      monthly: {
        regular: 19700,   // R$ 197,00
        launch: 9700,     // R$ 97,00 (lançamento ~50.8% desconto)
      },
      annual: {
        regular: 157600,  // R$ 1.576/ano (R$ 131,33/mês - 2 meses grátis)
        launch: 77600,    // R$ 776/ano (R$ 64,67/mês)
      }
    },
    
    limits: {
      imoveis: 50,
      clientes: 200,
      usuarios: 5,
      documentos: 500,
      whiteLabel: false,
      api: true,
      suporte: 'prioritario'
    },
    
    features: [
      { text: 'Até 50 imóveis', included: true },
      { text: 'Até 200 clientes', included: true },
      { text: '5 usuários', included: true },
      { text: 'Calendário de visitas', included: true },
      { text: 'Relatórios avançados', included: true },
      { text: 'API de integração', included: true },
      { text: 'Suporte prioritário', included: true },
      { text: 'Exportação Excel/PDF', included: true },
      { text: 'White Label', included: false },
    ],
    
    metadata: {
      isPopular: true,
      isBestValue: false,
      badge: 'MAIS POPULAR',
      cta: 'Assinar Profissional',
      highlight: 'Escolhido por 73% dos clientes',
      icon: '💼'
    }
  },
  
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    subtitle: 'Solução corporativa',
    color: 'emerald',
    
    prices: {
      monthly: {
        regular: 49700,   // R$ 497,00
        launch: 24700,    // R$ 247,00 (lançamento ~50.3% desconto)
      },
      annual: {
        regular: 397600,  // R$ 3.976/ano (R$ 331,33/mês - 2 meses grátis)
        launch: 197600,   // R$ 1.976/ano (R$ 164,67/mês)
      }
    },
    
    limits: {
      imoveis: Infinity,
      clientes: Infinity,
      usuarios: Infinity,
      documentos: Infinity,
      whiteLabel: true,
      api: true,
      suporte: 'dedicado'
    },
    
    features: [
      { text: 'Imóveis ilimitados', included: true },
      { text: 'Clientes ilimitados', included: true },
      { text: 'Usuários ilimitados', included: true },
      { text: 'Relatórios enterprise + BI', included: true },
      { text: 'API de integração', included: true },
      { text: 'Suporte dedicado', included: true },
      { text: 'White Label completo', included: true },
      { text: 'Domínio personalizado', included: true },
      { text: 'Onboarding dedicado', included: true },
    ],
    
    metadata: {
      isPopular: false,
      isBestValue: true,
      badge: 'MELHOR VALOR',
      cta: 'Falar com vendas',
      highlight: 'Melhor custo-benefício',
      icon: '👑'
    }
  }
};

/**
 * ════════════════════════════════════════════
 * VALIDAÇÃO
 * ════════════════════════════════════════════
 */

/**
 * Verificar se um plano existe
 * @param {string} planId - ID do plano
 * @returns {boolean}
 */
export function isValidPlan(planId) {
  return Boolean(planId && PLANS[planId]);
}

/**
 * Obter todos os IDs de planos válidos
 * @returns {string[]}
 */
export function getPlanIds() {
  return Object.keys(PLANS);
}

/**
 * ════════════════════════════════════════════
 * HELPERS DE PREÇO (padrão enterprise)
 * ════════════════════════════════════════════
 */

/**
 * Obter preço atual (considera lançamento se disponível)
 * @param {string} planId - ID do plano
 * @param {string} billing - 'monthly' | 'annual'
 * @param {boolean} isLaunchAvailable - Se ainda há cupons de lançamento
 * @returns {number} Preço em centavos. Retorna 0 se plano não existe.
 */
export function getPrice(planId, billing = 'monthly', isLaunchAvailable = false) {
  const plan = PLANS[planId];
  if (!plan) {
    console.warn(`⚠️ getPrice: Plano "${planId}" não encontrado`);
    return 0;
  }
  
  // Validar billing cycle
  const validBilling = ['monthly', 'annual'].includes(billing) ? billing : 'monthly';
  const prices = plan.prices[validBilling] || plan.prices.monthly;
  
  // Preço de lançamento (se disponível e há cupons)
  if (isLaunchAvailable && prices.launch) {
    return prices.launch;
  }
  
  return prices.regular;
}

/**
 * Obter preço formatado para exibição
 * @param {number} cents - Preço em centavos
 * @returns {string} Preço formatado (ex: "R$ 97,00")
 */
export function formatPrice(cents) {
  if (typeof cents !== 'number' || isNaN(cents) || cents < 0) {
    return 'R$ 0,00';
  }
  const reais = cents / 100;
  return reais.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

/**
 * Obter preço mensal equivalente (para planos anuais)
 * @param {number} annualCents - Preço anual em centavos
 * @returns {number} Preço mensal equivalente em centavos
 */
export function getMonthlyEquivalent(annualCents) {
  if (typeof annualCents !== 'number' || isNaN(annualCents) || annualCents <= 0) {
    return 0;
  }
  return Math.round(annualCents / 12);
}

/**
 * Calcular economia anual vs mensal
 * @param {number} monthlyCents - Preço mensal em centavos
 * @param {number} annualCents - Preço anual em centavos
 * @returns {Object} { savingsCents, savingsPercentage }
 */
export function getAnnualSavings(monthlyCents, annualCents) {
  const yearlyMonthlyTotal = monthlyCents * 12;
  const savingsCents = yearlyMonthlyTotal - annualCents;
  const savingsPercentage = Math.round((savingsCents / yearlyMonthlyTotal) * 100);
  
  return { savingsCents, savingsPercentage };
}

/**
 * ════════════════════════════════════════════
 * HELPERS DE PLANO
 * ════════════════════════════════════════════
 */

/**
 * Obter features de um plano
 * @param {string} planId - ID do plano
 * @returns {Array} Lista de features (fallback: plano basic)
 */
export function getPlanFeatures(planId) {
  return PLANS[planId]?.features || PLANS.basic.features;
}

/**
 * Obter limites de um plano
 * @param {string} planId - ID do plano
 * @returns {Object} Limites (fallback: plano basic)
 */
export function getPlanLimits(planId) {
  return PLANS[planId]?.limits || PLANS.basic.limits;
}

/**
 * Verificar se é plano de lançamento
 * @param {string} planId - ID do plano
 * @param {number} soldCount - Quantidade vendida (do Firestore)
 * @returns {boolean}
 */
export function isLaunchAvailable(planId, soldCount = 0) {
  const plan = PLANS[planId];
  if (!plan) return false;
  
  // Se não tem preço de lançamento, não está em lançamento
  if (!plan.prices?.monthly?.launch) return false;
  
  // Validar soldCount
  const count = Math.max(0, parseInt(soldCount) || 0);
  
  return count < LAUNCH_LIMIT;
}

/**
 * ════════════════════════════════════════════
 * HELPERS DE EXIBIÇÃO (UI)
 * ════════════════════════════════════════════
 */

/**
 * Obter todos os planos para exibição no frontend
 * Usado por: api/plans.js, PlanosPage.jsx (fallback)
 * 
 * @param {Object} launchCounters - Contadores de lançamento do Firestore
 * @returns {Array} Planos formatados para UI com TODOS os preços calculados
 */
export function getPlansForDisplay(launchCounters = {}) {
  return Object.values(PLANS).map(plan => {
    const sold = Math.max(0, parseInt(launchCounters[plan.id]) || 0);
    const launch = isLaunchAvailable(plan.id, sold);
    
    // Preços calculados
    const monthlyPrice = getPrice(plan.id, 'monthly', launch);
    const annualPrice = getPrice(plan.id, 'annual', launch);
    const monthlyEquivalent = getMonthlyEquivalent(annualPrice);
    const annualSavings = getAnnualSavings(
      plan.prices.monthly.regular,
      annualPrice
    );
    
    return {
      id: plan.id,
      name: plan.name,
      subtitle: plan.subtitle,
      color: plan.color,
      
      // Preços em centavos (para cálculos)
      monthlyPrice,
      annualPrice,
      monthlyEquivalent,
      
      // Preços formatados (para exibição)
      formattedMonthly: formatPrice(monthlyPrice),
      formattedAnnual: formatPrice(annualPrice),
      formattedMonthlyEquivalent: formatPrice(monthlyEquivalent),
      
      // Preços originais (para mostrar "De R$ X" com line-through)
      prices: {
        monthly: {
          regular: plan.prices.monthly.regular,
          launch: plan.prices.monthly.launch
        },
        annual: {
          regular: plan.prices.annual.regular,
          launch: plan.prices.annual.launch
        }
      },
      
      // Economia anual
      annualSavings: {
        amount: annualSavings.savingsCents,
        percentage: annualSavings.savingsPercentage,
        formatted: formatPrice(annualSavings.savingsCents)
      },
      
      // Features e limites
      features: plan.features,
      limits: plan.limits,
      
      // Status de lançamento
      isLaunch: launch,
      launchRemaining: Math.max(0, LAUNCH_LIMIT - sold),
      launchSold: sold,
      launchLimit: LAUNCH_LIMIT,
      
      // Metadados
      metadata: plan.metadata
    };
  });
}

/**
 * Obter plano para resposta da API
 * Usado por: endpoints que precisam de dados resumidos
 * 
 * @param {string} planId - ID do plano
 * @param {Object} launchCounters - Contadores do Firestore
 * @param {string} billing - 'monthly' | 'annual' (default: 'monthly')
 * @returns {Object|null} Dados do plano para API
 */
export function getPlanForAPI(planId, launchCounters = {}, billing = 'monthly') {
  const plan = PLANS[planId];
  if (!plan) return null;
  
  const sold = Math.max(0, parseInt(launchCounters[planId]) || 0);
  const launch = isLaunchAvailable(planId, sold);
  const validBilling = ['monthly', 'annual'].includes(billing) ? billing : 'monthly';
  const price = getPrice(planId, validBilling, launch);
  
  return {
    id: plan.id,
    name: plan.name,
    subtitle: plan.subtitle,
    color: plan.color,
    price,
    formattedPrice: formatPrice(price),
    currency: 'BRL',
    billing: validBilling,
    features: plan.features.filter(f => f.included).map(f => f.text),
    allFeatures: plan.features,
    limits: plan.limits,
    isLaunch: launch,
    launchRemaining: Math.max(0, LAUNCH_LIMIT - sold),
    launchSold: sold,
    launchLimit: LAUNCH_LIMIT,
    metadata: plan.metadata
  };
}

/**
 * ════════════════════════════════════════════
 * EXPORT DEFAULT (para consumidores que preferem)
 * ════════════════════════════════════════════
 */
export default {
  PLANS,
  LAUNCH_LIMIT,
  isValidPlan,
  getPlanIds,
  getPrice,
  formatPrice,
  getMonthlyEquivalent,
  getAnnualSavings,
  getPlanFeatures,
  getPlanLimits,
  isLaunchAvailable,
  getPlansForDisplay,
  getPlanForAPI
};